import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/components/auth/AuthProvider';
import { ArrowRight, CheckCircle, Target, Activity, Camera, BarChart3, User } from 'lucide-react';
import { OnboardingProfileForm } from './OnboardingProfileForm';
import { SimpleRunningForm } from '@/components/running/SimpleRunningForm';
import { UnifiedBodyCompositionForm } from '@/components/body-composition/UnifiedBodyCompositionForm';
import { PhotoUploadSection } from '@/components/photos/PhotoUploadSection';

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  action: string;
  route: string;
  completed?: boolean;
}

const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: 'profile',
    title: 'Set Up Your Profile',
    description: 'Tell us about yourself to personalize your fitness journey',
    icon: User,
    action: 'Complete Profile',
    route: '/profile'
  },
  {
    id: 'welcome',
    title: 'Welcome to Fitness Tracker!',
    description: 'Track your fitness journey with detailed analytics and progress photos',
    icon: Target,
    action: 'Get Started',
    route: '/dashboard'
  },
  {
    id: 'first_run',
    title: 'Log Your First Run',
    description: 'Start tracking your running performance with our guided form',
    icon: Activity,
    action: 'Add Run',
    route: '/running'
  },
  {
    id: 'body_comp',
    title: 'Track Body Composition',
    description: 'Monitor your body metrics and progress over time',
    icon: BarChart3,
    action: 'Add Metrics',
    route: '/body-composition'
  },
  {
    id: 'photos',
    title: 'Upload Progress Photos',
    description: 'Visual progress tracking with before/after comparisons',
    icon: Camera,
    action: 'Add Photos',
    route: '/photos'
  }
];

interface OnboardingFlowProps {
  open: boolean;
  onComplete: () => void;
}

export const OnboardingFlow: React.FC<OnboardingFlowProps> = ({ open, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);

  const handleNext = () => {
    if (currentStep < ONBOARDING_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      // Mark onboarding as complete
      localStorage.setItem('onboardingCompleted', 'true');
      onComplete();
    }
  };

  const handleSkip = () => {
    localStorage.setItem('onboardingCompleted', 'true');
    onComplete();
  };

  const handleProfileComplete = () => {
    setCurrentStep(1); // Move to welcome step after profile is complete
  };

  const currentStepData = ONBOARDING_STEPS[currentStep];
  const progress = ((currentStep + 1) / ONBOARDING_STEPS.length) * 100;

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">{/* Can't prevent close, but onboarding handles completion */}
        <DialogHeader>
          <DialogTitle className="text-center text-2xl">
            Welcome to Your Fitness Journey! 🎯
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>Step {currentStep + 1} of {ONBOARDING_STEPS.length}</span>
              <span>{Math.round(progress)}% complete</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          {/* Current Step */}
          {currentStep === 0 ? (
            <OnboardingProfileForm 
              onComplete={handleProfileComplete}
              onSkip={handleSkip}
            />
          ) : currentStep === 1 ? (
            <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-primary/20 rounded-full">
                    <currentStepData.icon className="h-8 w-8 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-2">{currentStepData.title}</h3>
                    <p className="text-muted-foreground mb-4">{currentStepData.description}</p>
                    
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 bg-background rounded-lg border">
                          <div className="flex items-center gap-2 text-sm">
                            <Activity className="h-4 w-4 text-blue-600" />
                            <span>Track runs & performance</span>
                          </div>
                        </div>
                        <div className="p-3 bg-background rounded-lg border">
                          <div className="flex items-center gap-2 text-sm">
                            <BarChart3 className="h-4 w-4 text-green-600" />
                            <span>Monitor body composition</span>
                          </div>
                        </div>
                        <div className="p-3 bg-background rounded-lg border">
                          <div className="flex items-center gap-2 text-sm">
                            <Camera className="h-4 w-4 text-purple-600" />
                            <span>Progress photos</span>
                          </div>
                        </div>
                        <div className="p-3 bg-background rounded-lg border">
                          <div className="flex items-center gap-2 text-sm">
                            <Target className="h-4 w-4 text-orange-600" />
                            <span>Set & track goals</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : currentStep === 2 ? (
            <div>
              <div className="mb-4 text-center">
                <h3 className="text-xl font-semibold mb-2">{currentStepData.title}</h3>
                <p className="text-muted-foreground">{currentStepData.description}</p>
              </div>
              <SimpleRunningForm />
            </div>
          ) : currentStep === 3 ? (
            <div>
              <div className="mb-4 text-center">
                <h3 className="text-xl font-semibold mb-2">{currentStepData.title}</h3>
                <p className="text-muted-foreground">{currentStepData.description}</p>
              </div>
              <UnifiedBodyCompositionForm />
            </div>
          ) : currentStep === 4 ? (
            <div>
              <div className="mb-4 text-center">
                <h3 className="text-xl font-semibold mb-2">{currentStepData.title}</h3>
                <p className="text-muted-foreground">{currentStepData.description}</p>
              </div>
              <PhotoUploadSection />
            </div>
          ) : null}

          {/* Step Overview */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {ONBOARDING_STEPS.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = index === currentStep;
              const isCompleted = index < currentStep;
              
              return (
                <div
                  key={step.id}
                  className={`p-3 rounded-lg border text-center transition-all ${
                    isActive 
                      ? 'border-primary bg-primary/10' 
                      : isCompleted 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-border bg-muted/30'
                  }`}
                >
                  <div className={`inline-flex p-2 rounded-full mb-2 ${
                    isActive 
                      ? 'bg-primary text-primary-foreground' 
                      : isCompleted 
                        ? 'bg-green-500 text-white' 
                        : 'bg-muted text-muted-foreground'
                  }`}>
                    {isCompleted ? (
                      <CheckCircle className="h-4 w-4" />
                    ) : (
                      <StepIcon className="h-4 w-4" />
                    )}
                  </div>
                  <div className={`text-xs font-medium ${
                    isActive ? 'text-primary' : isCompleted ? 'text-green-700' : 'text-muted-foreground'
                  }`}>
                    {step.title.split(' ').slice(0, 2).join(' ')}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Navigation - Only show for non-profile steps */}
          {currentStep !== 0 && (
            <div className="flex justify-between pt-4">
              <Button variant="ghost" onClick={handleSkip}>
                Skip for now
              </Button>
              <Button onClick={handleNext} className="min-w-[120px]">
                {currentStep === ONBOARDING_STEPS.length - 1 ? 'Get Started!' : 'Next'}
                <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Hook to check if user needs onboarding
export const useOnboarding = () => {
  const { user } = useAuth();
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    if (user) {
      const completed = localStorage.getItem('onboardingCompleted');
      if (!completed) {
        setShowOnboarding(true);
      }
    }
  }, [user]);

  return {
    showOnboarding,
    completeOnboarding: () => setShowOnboarding(false)
  };
};